// src/components/PersonalDataForm.js
import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import InputMask from 'react-input-mask';
import Button from './Button/Button';
import Avatar from './Avatar/Avatar';
import H2 from './H2/H2';
import MessagePopup from './MessagePopup/MessagePopup';
import './PersonalDataForm.css';

const MAX_AVATAR_SIZE_BYTES = 3 * 1024 * 1024; // 3 MB
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const getExtension = (file) => {
  const fromName = file.name && file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (fromName) return fromName;
  if (file.type === 'image/jpeg') return 'jpg';
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  if (file.type === 'image/gif') return 'gif';
  return 'img';
};

const USER_TYPES = {
  MIGRANTE_REFUGIADO: 'migrante_refugiado',
  REPRESENTANTE_ORGANIZACAO: 'representante_organizacao',
  APOIADOR: 'apoiador',
};

const SITUATION_OPTIONS = [
  { value: 'refugiado', label: 'Refugiado(a) reconhecido(a)' },
  { value: 'solicitante_refugio', label: 'Solicitante de refúgio' },
  { value: 'migrante', label: 'Migrante' },
  { value: 'apatrida', label: 'Apátrida' },
  { value: 'outro', label: 'Outro' },
];

const STATUS_OPTIONS = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'em_pausa', label: 'Em pausa' },
  { value: 'nao_disponivel', label: 'Não disponível' },
];

// Initialize all fields to ensure they are controlled
const initialUserData = {
  firstName: '',
  lastName: '',
  institutionName: '',
  cnpj: '',
  birthDate: '',
  gender: '',
  email: '',
  mobile: '',
  phone: '',
  password: '',
  about: '',
  avatar: '',
  userType: '',
  nationality: '',
  situation: '',
  status: 'ativo',
  emailConsent: false,
  newsletterConsent: false,
};

const sanitizeCnpj = (value = '') => value.replace(/\D/g, '');

// Converte o createdAt do Firestore (Timestamp, {seconds} ou string) em data legível.
const formatCreatedAt = (value) => {
  if (!value) return '—';
  let date;
  if (typeof value.toDate === 'function') date = value.toDate();
  else if (value.seconds) date = new Date(value.seconds * 1000);
  else date = new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR');
};

const PersonalDataForm = ({ userId, setShowPopup }) => {
  const [userData, setUserData] = useState(initialUserData);
  const [charCount, setCharCount] = useState(0);
  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState({ title: '', message: '' });
  const [uploadProgress, setUploadProgress] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    const fetchUserData = async () => {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        setUserData({ ...initialUserData, ...userSnap.data() });
      } else {
        setMessageContent({ title: 'Error', message: 'No such document!' });
        setShowMessage(true);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  const handleInputChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleNameChange = (e) => {
    // Only allow letters
    const regex = /^[A-Za-z ]+$/;
    if (e.target.value === '' || regex.test(e.target.value)) {
      setUserData({ ...userData, [e.target.name]: e.target.value });
    }
  };
  
  const handleEmailChange = (e) => {
    const email = e.target.value;
    // Simple email regex validation
    const regex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
    if (email === '' || regex.test(email)) {
      setUserData({ ...userData, [e.target.name]: email });
    }
  };

  const handlePhoneChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleAboutChange = (event) => {
    setUserData({ ...userData, about: event.target.value });
    setCharCount(event.target.value.length);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      // Para usuários virando representantes de organização: exige CNPJ e nome da instituição.
      if (
        userData.userType === USER_TYPES.REPRESENTANTE_ORGANIZACAO &&
        (!userData.cnpj || !userData.institutionName)
      ) {
        setMessageContent({
          title: 'Minha conta',
          message: 'Informe o CNPJ e o nome da instituição para se cadastrar como representante de organização.',
        });
        setShowMessage(true);
        return;
      }

      const userRef = doc(db, 'users', userId);
      // eslint-disable-next-line no-unused-vars
      const { password, ...dataToUpdate } = userData;

      // Garante vínculo com organização (cria ou entra como membro).
      if (
        userData.userType === USER_TYPES.REPRESENTANTE_ORGANIZACAO &&
        !userData.organizationId
      ) {
        const cnpjClean = sanitizeCnpj(userData.cnpj);
        const orgQuery = query(
          collection(db, 'organizations'),
          where('cnpj', '==', cnpjClean)
        );
        const orgSnap = await getDocs(orgQuery);
        let organizationId;
        let organizationRole;
        if (!orgSnap.empty) {
          organizationId = orgSnap.docs[0].id;
          organizationRole = 'member';
        } else {
          const orgRef = await addDoc(collection(db, 'organizations'), {
            cnpj: cnpjClean,
            cnpjFormatted: userData.cnpj,
            name: userData.institutionName,
            ownerId: userId,
            createdAt: serverTimestamp(),
          });
          organizationId = orgRef.id;
          organizationRole = 'owner';
        }

        await addDoc(collection(db, 'organizationMembers'), {
          organizationId,
          userId,
          role: organizationRole,
          status: 'active',
          createdAt: serverTimestamp(),
        });

        dataToUpdate.organizationId = organizationId;
        dataToUpdate.organizationRole = organizationRole;
        dataToUpdate.type = 'institution';
      }

      // Deixou de ser representante de organização: limpa o vínculo e os campos relacionados.
      let removedOrgFields = false;
      if (
        userData.userType !== USER_TYPES.REPRESENTANTE_ORGANIZACAO &&
        userData.organizationId
      ) {
        try {
          const memberQuery = query(
            collection(db, 'organizationMembers'),
            where('userId', '==', userId),
            where('organizationId', '==', userData.organizationId)
          );
          const memberSnap = await getDocs(memberQuery);
          await Promise.all(memberSnap.docs.map((d) => deleteDoc(d.ref)));
        } catch (cleanupErr) {
          console.warn('Não foi possível remover o vínculo com a organização:', cleanupErr);
        }

        dataToUpdate.organizationId = deleteField();
        dataToUpdate.organizationRole = deleteField();
        dataToUpdate.type = deleteField();
        removedOrgFields = true;
      }

      await updateDoc(userRef, dataToUpdate);
      setUserData((prev) => {
        const next = { ...prev, ...dataToUpdate };
        if (removedOrgFields) {
          delete next.organizationId;
          delete next.organizationRole;
          delete next.type;
        }
        return next;
      });
      setMessageContent({ title: 'Minha conta', message: 'Dados atualizados com sucesso!' });
      setShowMessage(true);
    } catch (error) {
      console.error('Erro ao atualizar os dados:', error);
      setMessageContent({ title: 'Minha conta', message: 'Erro ao atualizar os dados.' });
      setShowMessage(true);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setMessageContent({
        title: 'Minha conta',
        message: 'Formato inválido. Use JPG, PNG, WEBP ou GIF.',
      });
      setShowMessage(true);
      return;
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      setMessageContent({
        title: 'Minha conta',
        message: 'A imagem deve ter no máximo 3 MB.',
      });
      setShowMessage(true);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    const extension = getExtension(file);
    const storageRef = ref(storage, `users/${userId}/avatar.${extension}`);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      cacheControl: 'public, max-age=3600',
    });

    setUploadProgress(0);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error('Erro ao fazer upload da foto:', error);
        setUploadProgress(null);
        setPreviewUrl('');
        URL.revokeObjectURL(objectUrl);
        setMessageContent({ title: 'Minha conta', message: 'Erro ao fazer upload da foto.' });
        setShowMessage(true);
      },
      async () => {
        try {
          const photoURL = await getDownloadURL(uploadTask.snapshot.ref);
          const previousAvatar = userData.avatar;
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, { avatar: photoURL });
          setUserData((prev) => ({ ...prev, avatar: photoURL }));

          if (
            previousAvatar &&
            typeof previousAvatar === 'string' &&
            previousAvatar.includes('/o/users%2F') &&
            !previousAvatar.includes(`avatar.${extension}`)
          ) {
            try {
              const oldPath = decodeURIComponent(previousAvatar.split('/o/')[1].split('?')[0]);
              await deleteObject(ref(storage, oldPath));
            } catch (cleanupErr) {
              console.warn('Não foi possível remover o avatar anterior:', cleanupErr);
            }
          }

          setMessageContent({ title: 'Minha conta', message: 'Foto atualizada com sucesso!' });
          setShowMessage(true);
        } catch (error) {
          console.error('Erro ao salvar URL da foto:', error);
          setMessageContent({ title: 'Minha conta', message: 'Erro ao salvar a foto.' });
          setShowMessage(true);
        } finally {
          setUploadProgress(null);
          setPreviewUrl('');
          URL.revokeObjectURL(objectUrl);
        }
      }
    );
  };

  const displayName = userData.firstName || userData.institutionName || '';

  return (
    <div className="max-w-screen-xl mx-auto px-5 mt-12 md:px-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-5">
        <div className="col-span-1 md:col-span-3 text-center flex flex-col items-center">
          <Avatar
            src={previewUrl || userData.avatar}
            alt="Avatar"
            name={displayName}
            large
            className="w-40 h-40 rounded-full bg-pink-600 flex justify-center items-center text-8xl font-bold mb-2.5"
          />
          {uploadProgress !== null && (
            <div className="w-40 mt-2">
              <div className="h-2 w-full bg-gray-200 rounded">
                <div
                  className="h-2 bg-purple-600 rounded transition-all"
                  style={{ width: `${Math.round(uploadProgress)}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">Enviando… {Math.round(uploadProgress)}%</p>
            </div>
          )}
          <label
            htmlFor="file-upload"
            className={`mt-4 ${uploadProgress !== null ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer text-blue-600'}`}
          >
            {uploadProgress !== null ? 'Enviando…' : 'Editar foto'}
          </label>
          <p>Envie sua foto de perfil com dimensões quadradas, idealmente 200x200 pixels ou mais. Máximo 3 MB (JPG, PNG, WEBP ou GIF).</p>
          <input
            id="file-upload"
            type="file"
            accept={ALLOWED_AVATAR_TYPES.join(',')}
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploadProgress !== null}
          />
        </div>

        <div className="col-span-1 md:col-span-9">
          <H2 text="Dados Pessoais" textColor="purple" />
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <label className="block">
                Primeiro nome
                <input
                  type="text"
                  name="firstName"
                  value={userData.firstName}
                  onChange={handleNameChange}
                  className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1"
                />
              </label>
              <label className="block">
                Sobrenome
                <input
                  type="text"
                  name="lastName"
                  value={userData.lastName}
                  onChange={handleNameChange}
                  className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1"
                />
              </label>
              <label className="block">
                Data de Nascimento
                <input type="date" name="birthDate" value={userData.birthDate} onChange={handleInputChange} className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1" />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <label className="block">
                Gênero
                <div className="select-container">
                  <select className="w-full h-12 border border-gray-300 rounded-lg pl-2.5 pr-10 mt-1 appearance-none" name="gender" value={userData.gender} onChange={handleInputChange}>
                    <option value="">Selecione</option>
                    <option value="masculino">Masculino</option>
                    <option value="feminino">Feminino</option>
                    <option value="nao-binario">Não-binário</option>
                    <option value="genero-fluido">Gênero-fluido</option>
                    <option value="transgenero">Transgênero</option>
                    <option value="intersexo">Intersexo</option>
                    <option value="prefiro-nao-informar">Prefiro não informar</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
              </label>
              <label className="block">
                Seu e-mail
                <input
                  type="email"
                  name="email"
                  value={userData.email}
                  onChange={handleEmailChange}
                  className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1"
                />
              </label>
              <label className="block">
                Celular
                <InputMask
                  mask="+55 (99) 99999-9999"
                  value={userData.mobile}
                  onChange={handlePhoneChange}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="text"
                      name="mobile"
                      className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1"
                    />
                  )}
                </InputMask>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <label className="block">
                Telefone fixo
                <InputMask
                  mask="+55 (99) 9999-9999"
                  value={userData.phone}
                  onChange={handlePhoneChange}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="text"
                      name="phone"
                      className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1"
                    />
                  )}
                </InputMask>
              </label>
              <label className="block">
                Sua senha
                <input type="password" name="password" value={userData.password} onChange={handleInputChange} className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1" />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <label className="block">
                Tipo de usuário
                <div className="select-container">
                  <select
                    name="userType"
                    value={userData.userType}
                    onChange={handleInputChange}
                    className="w-full h-12 border border-gray-300 rounded-lg pl-2.5 pr-10 mt-1 appearance-none"
                  >
                    <option value="">Selecione</option>
                    <option value={USER_TYPES.MIGRANTE_REFUGIADO}>Migrante / Refugiado(a)</option>
                    <option value={USER_TYPES.REPRESENTANTE_ORGANIZACAO}>Representante de organização</option>
                    <option value={USER_TYPES.APOIADOR}>Apoiador(a)</option>
                  </select>
                </div>
              </label>
              <label className="block">
                Status do perfil
                <div className="select-container">
                  <select
                    name="status"
                    value={userData.status || 'ativo'}
                    onChange={handleInputChange}
                    className="w-full h-12 border border-gray-300 rounded-lg pl-2.5 pr-10 mt-1 appearance-none"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="block">
                Nacionalidade
                <input
                  type="text"
                  name="nationality"
                  value={userData.nationality || ''}
                  onChange={handleInputChange}
                  className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1"
                />
              </label>
              <label className="block">
                Data de cadastro
                <input
                  type="text"
                  value={formatCreatedAt(userData.createdAt)}
                  readOnly
                  className="w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1 bg-gray-100 text-gray-700"
                />
              </label>
            </div>

            {userData.userType === USER_TYPES.MIGRANTE_REFUGIADO && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <label className="block">
                  Situação
                  <div className="select-container">
                    <select
                      name="situation"
                      value={userData.situation || ''}
                      onChange={handleInputChange}
                      className="w-full h-12 border border-gray-300 rounded-lg pl-2.5 pr-10 mt-1 appearance-none"
                    >
                      <option value="">Selecione</option>
                      {SITUATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </label>
              </div>
            )}

            {userData.userType === USER_TYPES.REPRESENTANTE_ORGANIZACAO && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <label className="block">
                  CNPJ
                  <input
                    type="text"
                    name="cnpj"
                    value={userData.cnpj || ''}
                    onChange={handleInputChange}
                    readOnly={Boolean(userData.organizationId)}
                    className={`w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1 ${userData.organizationId ? 'bg-gray-100 text-gray-700' : ''}`}
                  />
                </label>
                <label className="block">
                  Nome da instituição
                  <input
                    type="text"
                    name="institutionName"
                    value={userData.institutionName || ''}
                    onChange={handleInputChange}
                    readOnly={Boolean(userData.organizationId)}
                    className={`w-full h-12 border border-gray-300 rounded-lg px-2.5 mt-1 ${userData.organizationId ? 'bg-gray-100 text-gray-700' : ''}`}
                  />
                </label>
              </div>
            )}

            <div className="w-full mb-5">
              <label className="block">
                Sobre você <br />
                <textarea name="about" value={userData.about} onChange={handleAboutChange} maxLength="250" className="w-full h-36 p-2 border border-gray-300 rounded-md"></textarea>
              </label>
              <div className="flex justify-start">
                <p className="text-right text-gray-600">Caracteres {charCount}/250</p>
              </div>
            </div>

            <div className="w-full mb-5 border-t border-gray-200 pt-4">
              <p className="text-sm font-bold text-gray-700 mb-2">Comunicação por e-mail</p>
              <label className="flex items-start text-sm text-gray-700 mb-2">
                <input
                  type="checkbox"
                  name="emailConsent"
                  checked={Boolean(userData.emailConsent)}
                  onChange={(e) => setUserData((prev) => ({ ...prev, emailConsent: e.target.checked }))}
                  className="mt-1 mr-2"
                />
                Quero receber e-mails sobre as ofertas e demandas em que tenho interesse.
              </label>
              <label className="flex items-start text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="newsletterConsent"
                  checked={Boolean(userData.newsletterConsent)}
                  onChange={(e) => setUserData((prev) => ({ ...prev, newsletterConsent: e.target.checked }))}
                  className="mt-1 mr-2"
                />
                Quero receber a newsletter semanal com recomendações da Rede Refugia.
              </label>
            </div>

            <div className="flex justify-end">
              <Button text="Atualizar meus dados" variant="transparent" type="submit" />
            </div>
          </form>
        </div>
      </div>
      {showMessage && (
        <MessagePopup
          title={messageContent.title}
          message={messageContent.message}
          onClose={() => setShowMessage(false)}
        />
      )}
    </div>
  );
};

export default PersonalDataForm;

